import React, { FC, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowCircleRightIcon } from '@fronton/icons-react';
import { LinkButton, IconButton } from 'fronton-react';
import { getIsOnlineOnly, getStocksInStores } from 'features/goods-supply';
import { isProductSet, isWebSellable } from 'features/product-info';
import { cx } from 'linaria';
import { useTranslation } from 'react-i18next';
import { analyticsUtils } from 'utils';
import { Carousel } from 'common/components/carousel';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { useWindowSize } from '@navi-toolbox/hooks';
import { Skeleton } from 'fronton-react/unstable';
import { getHideServices } from 'features/page-model';
import { areRelatedServicesLoading, getRelatedServices, RELATED_SERVICE_ITEM_CLICK } from './ducks';
import type { RelatedServiceResponse } from './types';
import {
  carousel,
  carouselTablet,
  header,
  headerTopBorder,
  carouselContainerManyItems,
  titleSkeleton,
  buttonSkeleton,
  SkeletonTitleRow,
  SkeletonRows,
  ServicesSkeletonRow,
  serviceSkeleton,
  carouselContainer,
  moreTwoLinkList,
  linkList,
  RelatedContainer,
} from './styles';
import { RelatedServiceFactory } from './related-service-factory';

export const DATA_QA_RELATED_SERVICES_BUTTON = 'related-services-button-uslugi_mf-pdp';
export const DATA_QA_RELATED_SERVICES_ICON = 'related-services-icon-uslugi_mf-pdp';
export const DATA_QA_RELATED_SERVICES = 'related-services_mf-pdp';
export const DATA_QA_RELATED_SERVICES_CAROUSEL = 'related-services-carousel';

const clickHandler = (isSet: boolean) => {
  analyticsUtils.sendEvent('GA:clickButton', {
    elementBlock: isSet ? 'komplektyDetail / services' : 'productDetail / services',
    elementName: 'toUslugi',
    elementText: 'Помощь мастера',
    elementType: 'button',
  });
};

type RelatedServicesProps = {
  isVisible?: boolean;
};

export const RelatedServices: FC<RelatedServicesProps> = ({ isVisible }) => {
  const dispatch = useDispatch();
  const { width } = useWindowSize();
  const { t } = useTranslation();

  const stocks = useSelector(getStocksInStores);
  const isSellable = useSelector(isWebSellable);
  const isOnlineOnly = useSelector(getIsOnlineOnly);
  const isHideServices = useSelector(getHideServices);
  const isSet = useSelector(isProductSet);

  const hasEmptyStocksRecords = !stocks.some((stock) => stock.stockValue > 0);
  const hasDisplaySample = hasEmptyStocksRecords && stocks.some((stock) => stock.displaySample);
  const isRenderTopBorder = !hasDisplaySample && isSellable && (isOnlineOnly || hasEmptyStocksRecords);

  let data: RelatedServiceResponse[] = useSelector(getRelatedServices);
  let isLoading = useSelector(areRelatedServicesLoading);

  const service: RelatedServiceResponse | null = data?.length === 1 ? data[0] : null;

  const { mobile, tablet } = useBreakpoints() || { tablet: false };

  function onRelatedServiceClick(payload: RelatedServiceResponse) {
    dispatch(RELATED_SERVICE_ITEM_CLICK(payload));
  }

  const isColumnsTablet = tablet && data.length > 2;

  useEffect(() => {
    if (data?.length > 2) {
      const carouselEl = document.querySelector('[data-qa="related-services-carousel"]');
      const columns = carouselEl?.querySelectorAll('a');
      const firstColumn = carouselEl?.querySelectorAll('a')[0];

      if (firstColumn && columns) {
        columns.forEach((column, index) => {
          if (index) {
            column.style.width = `${firstColumn.clientWidth}px`;
          }
        });
      }
    }
  }, [data.length, mobile, tablet, width]);

  if (!data?.length) {
    return null;
  }

  const isManyItems = data?.length > 2;
  const isSingleItem = data?.length === 1;

  return isHideServices?.on ? null : (
    <RelatedContainer isVisible={isVisible} data-qa={DATA_QA_RELATED_SERVICES}>
      {isLoading ? (
        <SkeletonRows>
          <SkeletonTitleRow>
            <Skeleton className={titleSkeleton} />
            <Skeleton className={buttonSkeleton} />
          </SkeletonTitleRow>
          <ServicesSkeletonRow>
            <Skeleton className={serviceSkeleton} />
            <Skeleton className={serviceSkeleton} />
          </ServicesSkeletonRow>
        </SkeletonRows>
      ) : (
        <>
          <div className={cx(header, isRenderTopBorder ? headerTopBorder : null)}>
            <LinkButton
              href="/uslugi/"
              target="_blank"
              data-qa={DATA_QA_RELATED_SERVICES_BUTTON}
              size="s"
              onClick={() => clickHandler(isSet)}
              variant="accent"
            >
              {t('related-services.button')}
            </LinkButton>
            <IconButton
              href="/uslugi/"
              target="_blank"
              data-qa={DATA_QA_RELATED_SERVICES_ICON}
              label={t('related-services.button')}
              onClick={() => clickHandler(isSet)}
              size="l"
            >
              <ArrowCircleRightIcon type="outline" size="l" color="text-primary" />
            </IconButton>
          </div>
          {service ? (
            <RelatedServiceFactory
              service={service}
              onClick={() => onRelatedServiceClick({ ...service, index: 0 })}
              key={service.title}
              href={service.serviceHref}
              isManyItems={isManyItems}
              singleService={isSingleItem}
            />
          ) : (
            <Carousel
              className={cx(carousel, isColumnsTablet ? carouselTablet : null)}
              containerClassName={isManyItems ? carouselContainerManyItems : carouselContainer}
              data-qa={DATA_QA_RELATED_SERVICES_CAROUSEL}
            >
              {data?.map((item, index) => (
                <RelatedServiceFactory
                  vertical
                  key={item.title}
                  service={item}
                  href={item.serviceHref}
                  isManyItems={isManyItems}
                  singleService={isSingleItem}
                  className={cx(isManyItems ? moreTwoLinkList : undefined, linkList)}
                  onClick={() => onRelatedServiceClick({ ...item, index })}
                />
              ))}
            </Carousel>
          )}
        </>
      )}
    </RelatedContainer>
  );
};
